import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  signal,
  ViewChild,
  AfterViewChecked,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { AgentService, ChatSession, ChatMessage } from '../../services/agent.service';

type PanelView = 'chat' | 'history';

const SUGGESTED = [
  'How many anomalies were found in the last uploaded file?',
  'Show employees with billing hours exceeded this month.',
  'What is the gross profit for the latest period?',
  'Which fixed projects have Revenue Trend Mismatch anomalies?',
  'List top 10 cost centres by EUR amount this fiscal year.',
  'How many accruals were not carried forward?',
];

@Component({
  selector: 'app-ai-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './ai-chat.html',
  styleUrl: './ai-chat.scss',
})
export class AiChatComponent implements OnInit, AfterViewChecked {
  private agentSvc = inject(AgentService);

  @ViewChild('msgEnd') private msgEnd!: ElementRef<HTMLDivElement>;
  @ViewChild('inputEl') private inputEl!: ElementRef<HTMLTextAreaElement>;

  // ── UI state ─────────────────────────────────────────────────────
  isOpen = signal(false);
  panelView = signal<PanelView>('chat');
  isLoadingSessions = signal(false);
  isSending = signal(false);
  expandedSql = signal<string | null>(null);

  // ── Data ─────────────────────────────────────────────────────────
  sessions = signal<ChatSession[]>([]);
  activeSessionId = signal<string | null>(null);
  messages = signal<ChatMessage[]>([]);
  inputText = signal('');
  suggestions = SUGGESTED;
  private needsScroll = false;

  ngOnInit() {
    // Nothing on init — load history lazily when panel opens
  }

  // ── Panel toggle ────────────────────────────────────────────────
  toggle() {
    this.isOpen.set(!this.isOpen());
    if (this.isOpen() && this.sessions().length === 0) {
      this.loadSessions();
    }
    if (this.isOpen()) {
      setTimeout(() => this.inputEl?.nativeElement.focus(), 120);
    }
  }

  close() { this.isOpen.set(false); }

  showHistory() {
    this.panelView.set('history');
    this.loadSessions();
  }

  showChat() { this.panelView.set('chat'); }

  // ── Sessions ────────────────────────────────────────────────────
  loadSessions() {
    this.isLoadingSessions.set(true);
    this.agentSvc.getSessions()
      .pipe(finalize(() => this.isLoadingSessions.set(false)))
      .subscribe({
        next: (sessions) => this.sessions.set(sessions),
        error: () => this.sessions.set([]),
      });
  }

  openSession(session: ChatSession) {
    this.panelView.set('chat');
    this.activeSessionId.set(session.id);
    this.messages.set([]);
    this.agentSvc.getSession(session.id).subscribe({
      next: (detail) => {
        this.messages.set(detail.messages);
        this.needsScroll = true;
      },
    });
  }

  newChat() {
    this.activeSessionId.set(null);
    this.messages.set([]);
    this.panelView.set('chat');
    setTimeout(() => this.inputEl?.nativeElement.focus(), 120);
  }

  deleteSession(session: ChatSession, event: Event) {
    event.stopPropagation();
    this.agentSvc.deleteSession(session.id).subscribe({
      next: () => {
        this.sessions.update(s => s.filter(x => x.id !== session.id));
        if (this.activeSessionId() === session.id) {
          this.newChat();
        }
      },
    });
  }

  // ── Chat ────────────────────────────────────────────────────────
  sendSuggestion(q: string) {
    this.inputText.set(q);
    this.send();
  }

  send() {
    const question = this.inputText().trim();
    if (!question || this.isSending()) return;
    this.inputText.set('');
    this.isSending.set(true);

    const optimisticUser: ChatMessage = {
      id: 'temp-' + Date.now(),
      session_id: this.activeSessionId() ?? '',
      role: 'user',
      content: question,
      created_at: new Date().toISOString(),
    };
    this.messages.update(m => [...m, optimisticUser]);
    this.needsScroll = true;

    const doChat = (sessionId: string) => {
      this.agentSvc.chat(sessionId, question)
        .pipe(finalize(() => this.isSending.set(false)))
        .subscribe({
          next: (res) => {
            // Replace temp user message with real one
            this.messages.update(m => m.map(msg =>
              msg.id === optimisticUser.id
                ? { ...msg, id: res.user_message_id, session_id: sessionId }
                : msg
            ));
            const assistantMsg: ChatMessage = {
              id: res.assistant_message_id,
              session_id: sessionId,
              role: 'assistant',
              content: res.answer,
              sql_query: res.sql,
              db_used: res.db,
              row_count: res.row_count,
              columns: res.columns,
              rows: res.rows,
              is_error: res.error ? 'true' : 'false',
              created_at: new Date().toISOString(),
            };
            this.messages.update(m => [...m, assistantMsg]);
            this.needsScroll = true;

            // Refresh session list to update title/order
            this.loadSessions();
          },
          error: (err) => {
            const errMsg: ChatMessage = {
              id: 'err-' + Date.now(),
              session_id: sessionId,
              role: 'assistant',
              content: err?.error?.detail ?? 'Something went wrong. Please try again.',
              is_error: 'true',
              created_at: new Date().toISOString(),
            };
            this.messages.update(m => [...m, errMsg]);
            this.needsScroll = true;
          },
        });
    };

    if (this.activeSessionId()) {
      doChat(this.activeSessionId()!);
    } else {
      // Auto-create session on first message
      this.agentSvc.createSession().subscribe({
        next: (session) => {
          this.activeSessionId.set(session.id);
          this.sessions.update(s => [session, ...s]);
          doChat(session.id);
        },
        error: () => {
          this.isSending.set(false);
          this.messages.update(m => m.filter(x => x.id !== optimisticUser.id));
        },
      });
    }
  }

  onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  }

  toggleSql(msgId: string) {
    this.expandedSql.set(this.expandedSql() === msgId ? null : msgId);
  }

  get hasMessages() { return this.messages().length > 0; }
  get showSuggestions() { return !this.hasMessages && !this.isSending(); }
  get unreadCount() { return Math.floor(this.messages().length / 2); }

  ngAfterViewChecked() {
    if (this.needsScroll) {
      this.msgEnd?.nativeElement.scrollIntoView({ behavior: 'smooth' });
      this.needsScroll = false;
    }
  }

  trackById(_: number, item: any) { return item.id; }
  trackByIdx(i: number) { return i; }

  relativeTime(iso: string): string {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  }
}